/* global Promise */
/* eslint-disable no-underscore-dangle */

import queryString from 'query-string';

import sessionReader from '../../store/readHelpers/SessionReader';
import apiActions from '../../actions/ApiActions';
import { API_ROOT, methods, responseCategories, TEST_FILE } from './ApiConstants';
import storeManager from '../../store/StoreManager';
import environmentNames from '../../environmentNames';
import globalsService from '../../services/GlobalsService';
import navigationService from '../NavigationService';
import alertService from '../AlertService';
import { reportError } from '../ErrorReporting';
import sessionActions from '../../actions/SessionActions';
import fileDeliveryUploader from '../FileDeliveryUploader';
import deliveringActions from '../../actions/DeliveringActions';
import uiReader from '../../store/readHelpers/uiReader';
import messagesService from '../MessagesService';
import connectionStatusService from '../ConnectionStatusService';
import connectionStatusConstants from '../../constants/ConnectionStatusConstants';
import settingsReader from '../../store/readHelpers/SettingsReader';
import idleWarningService from '../IdleWarningService';
import sessionConstants from '../../constants/SessionConstants';

const { TRYING_TO_UPLOAD, ERRORED, PENDING } = deliveringActions.statuses;

const {
    STARTED_PRELOADING,
    RECONNECTING,
    CONNECTION_RESTORED,
    SAVED,
    DISCONNECTED,
    SERVER_ERROR,
    SYNCHRONISING,
    SHORT_DURATION,
    LONG_DURATION,
} = connectionStatusConstants;

require('isomorphic-fetch');

export const TIMEOUT_CONNECTION_RESTORED = 5000;

const apiTools = {
    async checkTestFile() {
        try {
            const { status } = await fetch(TEST_FILE, { cache: 'no-store' });
            window.console.log(`Fetched test file to check network connectivity. Got status ${status}.`);
            return status < 400;
        } catch (error) {
            reportError(error);
            return false;
        }
    },

    getDefaultInterval() {
        return process.env.NODE_ENV === environmentNames.UNIT_TESTING ? 50 : 5000;
    },

    getOfflineDeliveryLocalStorageKey() {
        const state = storeManager.getState();
        const assessmentRunContentId = state.assessment.assessmentRunContentId;
        const userId = state.session.sessionInfo.userId;
        return `${window.offlineDeliveryLocalStoragePrefixKey + userId}-${assessmentRunContentId}`;
    },

    getOfflineDashboardLink() {
        return window.offlineDashboardURL;
    },

    getTimeoutLimit() {
        return 30 * 1000;
    },

    getWindowLocation() {
        return window.location || undefined;
    },

    handleUnauthorizedResponse() {
        navigationService.navigateToDashboardTicket();
    },

    handleForbiddenResponse(response, requestUrl) {
        const ignorableMethods = [
            methods.GET_CORRECT_RESPONSES,
        ];

        const throwOutMethods = [
            methods.STORE_USER_RESPONSE,
            methods.CHECK_STORED_ITEMS,
            methods.DELIVER_BLANK,
            methods.DELIVER_TEST,
            methods.WITHDRAW,
            methods.GET_UPLOAD_TICKET,
            methods.GET_UPLOADER_INFO,
            methods.UPLOAD_SUCCESS,
            methods.GET_REMAINING_TIME,
            methods.GET_REMOTE_ORAL_EXAM_SESSION_INFO,
        ];

        if (ignorableMethods.find(method => requestUrl.includes(method))) {
            return;
        }

        if (throwOutMethods.find(method => requestUrl.includes(method))) {
            if (sessionReader.isDeliverInProgress()) {
                console.warn('Player is trying to navigate user away while test delivering is still in progress. Forbidden request url is - ',
                    requestUrl);
            }

            navigationService.navigateToDashboardTicket();
            return;
        }

        globalsService.setTestPlayerReady();
        storeManager.dispatch(sessionActions.setCriticalErrorOccurred(true));
        if (this.hasForbiddenItems(response)) {
            navigationService.navigateToPermissionDeniedItems();
            return;
        }
        navigationService.navigateToPermissionDenied();
    },

    isForbidden(status) {
        return status === 403;
    },

    hasForbiddenItems(response) {
        const { success, message } = response;
        return (
            success === false
            && message === 'hasForbiddenItems'
        );
    },

    /**
     * If the error is on the format returned by ApiService.executeRequest
     * @param error
     * @returns {boolean}
     */
    isResponseError(error) {
        // All errors that have come from the handleApiResponse have a response property defined
        // network failures are plain Error objects
        return Object.prototype.hasOwnProperty.call(error, 'errorType');
    },

    isRetryableError(error) {
        return ![
            responseCategories.NOT_LOGGED_IN,
            responseCategories.FORBIDDEN,
            responseCategories.WRONG_TOKEN,
            responseCategories.INVALID_TAB_TOKEN,
        ].includes(error.errorType);
    },

    isUnauthorized(status) {
        return status === 401;
    },

    makeGetRequest(url) {
        return {
            url,
            init: {
                credentials: 'same-origin',
                method: 'GET',
            },
        };
    },

    makeJsonPostRequest(url, data) {
        if (!data._token) {
            data._token = sessionReader.getCsrfToken();
        }

        return {
            url,
            init: {
                credentials: 'same-origin',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            },
        };
    },

    makeFormDataPostRequest(url, data = {}) {
        if (!data._token) {
            data._token = sessionReader.getCsrfToken();
        }

        const formData = queryString.stringify(data);
        const headers = new Headers();
        headers.append('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');

        return {
            url,
            init: {
                body: formData,
                credentials: 'same-origin',
                headers,
                method: 'POST',
            },
        };
    },

    prependAndAddIdentifier(apiMethod) {
        const qsId = sessionReader.getQuestionSetIdentifier();
        return this.prependDomain(`${API_ROOT}${apiMethod}?${qsId.paramName}=${qsId.paramValue}`);
    },

    /**
     * Prepends the https://www.mydomain.com part of the url to the path passed in.
     *
     * @param {string} path Must start with a /!
     * @returns {string}
     */
    prependDomain(path, location = apiTools.getWindowLocation()) {
        const { hostname, protocol, port } = location;

        if (!path?.startsWith('/')) {
            console.warn('Invalid path provided. Expecting to start with "/". Returning the original value.', path);
            return path;
        }

        let methodAndHost = `${protocol}//${hostname}`;

        const portAsInt = parseInt(port, 10);
        if (portAsInt > 0 && portAsInt !== 80 && portAsInt !== 443) {
            methodAndHost += `:${portAsInt}`;
        }

        return `${methodAndHost}${path}`;
    },

    async timeoutFetch(ms, url, init) {
        return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                reject({ errorType: responseCategories.TIMEOUT, response: undefined, error: undefined });
            }, ms);

            fetch(url, init)
                .then((res) => {
                    clearTimeout(timeoutId);
                    resolve(res);
                })
                .catch((error) => {
                    clearTimeout(timeoutId);
                    reject(error);
                });
        });
    },

    executeRequestWithRetry(request) {
        return this.executeRequest(request)
            .catch((error) => {
                if (this.isRetryableError(error)) {
                    return this.retryRequest(() => this.executeRequest(request));
                }
                return Promise.reject(error);
            })
            .catch(error => Promise.reject(error));
    },

    /**
     * Tries to execute a request.
     * Resolves with the parsed JSON body or rejects with normalized errors.
     * State is updated to reflect current network, server, login status when relevant.
     *
     * @param {Request} request
     * @return {Promise}
     */
    async executeRequest(request) {
        return this.timeoutFetch(this.getTimeoutLimit(), request.url, request.init)
            .then(response => this.handleApiResponse(response, request.url))
            .catch((error) => {
                window.console.log(`Error from handleApiResponse from url: ${request.url} with error: ${JSON.stringify(error)}`, error);

                // Error categories that don't warrant considering the client offline
                const onlineErrorCategories = [
                    responseCategories.INVALID_TAB_TOKEN,
                    responseCategories.WRONG_TOKEN,
                ];

                const isServerOK = error.errorType !== responseCategories.SERVER_ERROR;
                const isOnline = !isServerOK || onlineErrorCategories.includes(error.errorType);
                this.handlePossibleOnlineStatusChange(isOnline, isServerOK);

                if (this.isResponseError(error)) {
                    return Promise.reject(error);
                }
                // Either we are dealing with a TIMEOUT or a complete OFFLINE case - regardless
                // we are having issues reaching the server
                return Promise.reject({ errorType: responseCategories.OFFLINE, response: undefined, error });
            });
    },

    /**
     * Returns a Promise which resolves with the parsed JSON data or rejects with an object on this form:
     * {
     *      errorType,  // String
     *      error,      // Original Error, if any, else undefined
     *      response    // Original Response object
     * }
     * @param response
     * @param requestUrl
     * @returns {Promise}
     */
    handleApiResponse(response, requestUrl) {
        const { status } = response; // Since the native response has some read only properties we want to override.

        const isUnauthorized = this.isUnauthorized(status);
        const isForbidden = this.isForbidden(status);

        if (isForbidden || isUnauthorized) {
            // if getting 401 or 403 response from the server, you are online
            this.handlePossibleOnlineStatusChange(true, true);
            if (isForbidden) {
                this.handleForbiddenResponse(response, requestUrl);
                return Promise.reject({ errorType: responseCategories.FORBIDDEN, response });
            }
            this.handleUnauthorizedResponse();
            return Promise.reject({ errorType: responseCategories.NOT_LOGGED_IN, response });
        }

        return new Promise(async (resolve, reject) => {
            let errorType;
            let error;

            // Hack for (older?) Edge (versions?)
            if (!response.url) {
                try {
                    response.url = requestUrl;
                } catch (e) {
                    // Do noting. It is not that critical to not have the pesky response.url.
                }
            }

            if (status === 504) {
                const networkOk = await this.checkTestFile();
                if (networkOk) {
                    errorType = responseCategories.SERVER_ERROR;
                } else {
                    errorType = responseCategories.HTTP_ERROR;
                }
            } else if ([500, 501, 503].includes(status)) {
                errorType = responseCategories.SERVER_ERROR;
            } else if (status === 409) {
                errorType = responseCategories.WRONG_TOKEN;
            } else if (status === 432) {
                errorType = responseCategories.INVALID_TAB_TOKEN;
            } else if (status !== 200) {
                errorType = responseCategories.HTTP_ERROR;
            }

            if (!errorType) {
                try {
                    response.json()
                        .then((parsedData) => {
                            if (this.hasForbiddenItems(parsedData)) {
                                this.handleForbiddenResponse(parsedData, requestUrl);
                                reject({ errorType: responseCategories.FORBIDDEN, response });
                            }
                            // We have a 200 response, so we are online and servers are responding
                            this.handlePossibleOnlineStatusChange(true, true);
                            resolve(parsedData);
                        })
                        .catch((ex) => {
                            reject({ errorType: responseCategories.RESPONSE_PARSING_ERROR, response, error: ex });
                        });
                } catch (ex) {
                    error = ex;
                    errorType = responseCategories.RESPONSE_PARSING_ERROR;
                }
            }

            if (errorType) {
                if (errorType === responseCategories.WRONG_TOKEN) {
                    alertService.showInvalidTokenAlert();
                } else {
                    reject({ errorType, error, response });
                }
            }
        });
    },

    /**
     * Retries a request a given number of times, with the specified delay in between attempts
     *
     * @param {Function} requestFunction Any function that returns a promise.
     * @param retriesToDo
     * @param msToWait
     * @returns {Promise}
     */
    retryRequest(requestFunction, retriesToDo = 2, msToWait = this.getDefaultInterval()) {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                requestFunction()
                    .then((res) => {
                        resolve(res);
                    })
                    .catch((error) => {
                        const retriesLeft = retriesToDo - 1;
                        if (
                            retriesLeft > 0
                            && this.isRetryableError(error)
                        ) {
                            this.retryRequest(requestFunction, retriesLeft, msToWait)
                                .catch(() => {
                                    // No need to do anything. This is just to prevent unhandled rejection errors.
                                });
                        }

                        reject(error);
                    });
            }, msToWait);
        });
    },

    handleConnectionRestored() {
        storeManager.dispatch(apiActions.setServerErrorStartTimestamp(null));
        const { delivering } = storeManager.getState();
        if (delivering.offlineFileDeliveryStatus && Object.keys(delivering.offlineFileDeliveryStatus).length > 0) {
            return this.checkOfflineFileDeliveryStatus();
        }
        return true;
    },

    checkOfflineFileDeliveryStatus() {
        const { delivering } = storeManager.getState();
        const pendingOrUploadingDeliveries = fileDeliveryUploader
            .getFileDeliveriesByStatus(delivering.offlineFileDeliveryStatus, [TRYING_TO_UPLOAD, PENDING]);

        if (pendingOrUploadingDeliveries.length > 0) {
            return setTimeout(() => {
                this.checkOfflineFileDeliveryStatus();
            }, 1000);
        }
        const erroredFileDeliveries = fileDeliveryUploader.getFileDeliveriesByStatus(delivering.offlineFileDeliveryStatus, [ERRORED]);
        if (erroredFileDeliveries.length > 0) {
            return storeManager.dispatch(apiActions.setApiConnectionStatus());
        }
        return connectionStatusService.addStatusToQueue(SAVED, SHORT_DURATION);
    },

    handleParentChildConnectionRestored() {
        const { delivering } = storeManager.getState();
        const pendingFileDeliveries = fileDeliveryUploader.getPendingFileDeliveries(delivering.offlineFileDeliveryStatus);
        const status = pendingFileDeliveries.length > 0 ? SYNCHRONISING : CONNECTION_RESTORED;
        connectionStatusService.addStatusToQueue(
            status,
            TIMEOUT_CONNECTION_RESTORED,
            this.handleConnectionRestored.bind(this)
        );
    },

    triggerOnlineStatusMessages(onLineStatus, serverStatus) {
        if (onLineStatus.ok && serverStatus.ok) {
            clearTimeout(this.disconnectedTimeOut);
            if (sessionReader.isParentTestContextOutsideChildTest()) {
                this.handleParentChildConnectionRestored();
            } else {
                connectionStatusService.addStatusToQueue(
                    CONNECTION_RESTORED,
                    TIMEOUT_CONNECTION_RESTORED,
                    this.handleConnectionRestored.bind(this)
                );
            }
        } else if (!onLineStatus.ok) {
            connectionStatusService.forceStatus(RECONNECTING, -1);
            this.disconnectedTimeOut = setTimeout(() => {
                connectionStatusService.forceStatus(DISCONNECTED, -1);
                alertService.showConnectionLostAlert();
            }, 60000);
        } else {
            connectionStatusService.forceStatus(RECONNECTING, -1);
            this.disconnectedTimeOut = setTimeout(() => {
                connectionStatusService.forceStatus(SERVER_ERROR, -1);
                alertService.showServerErrorAlert();
                storeManager.dispatch(apiActions.setServerErrorStartTimestamp(Date.now()));
            }, 60000);
        }
    },

    handleIdleWarningTimer(onlineStatus, serverStatus) {
        if (!settingsReader.isIdleStateWarningEnabled()) {
            return;
        }
        if (!onlineStatus.ok || !serverStatus.ok) {
            idleWarningService.pauseTimer();
        }
        if (onlineStatus.ok && serverStatus.ok) {
            idleWarningService.startTimer();
        }
    },

    triggerPreloadingStatusInHeader(isPreloading) {
        if (uiReader.getThemeVariableValue('header__hidePreloadCaption')) {
            return;
        }

        if (isPreloading) {
            connectionStatusService.addStatusToQueue(STARTED_PRELOADING, LONG_DURATION);
        }
        storeManager.dispatch(apiActions.setPreloadingStatus(isPreloading));
    },

    compareAndAlertMessagesTimeout: undefined,

    async handlePossibleOnlineStatusChange(isOnline, isServerOK) {
        const { api } = storeManager.getState();
        const onlineStatus = { ok: isOnline, changed: isOnline !== api.isOnline };
        const serverStatus = { ok: isServerOK, changed: isServerOK !== api.isServerOK };

        if (onlineStatus.changed || serverStatus.changed) {
            storeManager.dispatch(apiActions.setIsOnline(isOnline));
            storeManager.dispatch(apiActions.setIsServerOK(isServerOK));
            this.triggerOnlineStatusMessages(onlineStatus, serverStatus);
            this.handleIdleWarningTimer(onlineStatus, serverStatus);
            if (isOnline && isServerOK && !sessionReader.isPublicExam() && !sessionReader.isDemoExam()) {
                clearTimeout(this.compareAndAlertMessagesTimeout);
                this.compareAndAlertMessagesTimeout = setTimeout(() => {
                    messagesService.fetchCompareAndAlertMessages();
                }, 5000);
            }
        }
    },

    appendViewAsForGetContentApis(url, isViewAsCandidate = sessionReader.isViewAsCandidate()) {
        if (isViewAsCandidate) {
            const paramSeparator = url.includes('?') ? '&' : '?';
            return `${url}${paramSeparator}viewAs=${sessionConstants.VIEW_AS_CANDIDATE}`;
        }
        return url;
    },

    async makeFakeRequest(fakeUrl) {
        const { protocol } = apiTools.getWindowLocation();
        let url = `${protocol}//${fakeUrl}`;
        if (fakeUrl.includes('http')) {
            url = fakeUrl;
        }

        return new Promise((resolve) => {
            try {
                fetch(url, {
                    method: 'GET',
                    mode: 'no-cors',
                    credentials: 'same-origin',
                })
                    .then(() => {
                        // just resolve promise
                        resolve();
                    })
                    .catch(() => {
                        // catch to prevent error logging
                        resolve();
                    });
            } catch (error) {
                // catch to prevent error logging
                resolve();
            }
        });
    },
};

export default apiTools;
